import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { createLogger } from '../../utils/logger';
const logger = createLogger('AccessChecklist');

import {
  accessChecklistService,
  AccessChecklistByPerson,
  AccessChecklistByTask,
} from '../../services/accessChecklist';

type ViewMode = 'by-person' | 'by-task';

export const AccessChecklistScreen = ({ navigation }: any) => {
  const [viewMode, setViewMode] = useState<ViewMode>('by-person');
  const [byPersonData, setByPersonData] = useState<AccessChecklistByPerson[]>([]);
  const [byTaskData, setByTaskData] = useState<AccessChecklistByTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [personData, taskData] = await Promise.all([
        accessChecklistService.getChecklistByPerson(),
        accessChecklistService.getChecklistByTask(),
      ]);

      setByPersonData(personData);
      setByTaskData(taskData);
    } catch (error) {
      logger.error('Failed to load access checklist:', error);
      Alert.alert('Error', 'Failed to load access checklist');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleRevokeGrant = (
    grantId: string,
    personName: string,
    taskName: string,
  ) => {
    Alert.alert(
      'Revoke Access',
      `Remove ${personName}'s access to ${taskName}?\n\nThey will no longer be able to view this information.`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Remove Access',
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await accessChecklistService.revokeGrant(grantId);

              if (result.success) {
                Alert.alert('Success', result.message);
                loadData(); // Reload data
              }
            } catch (error) {
              logger.error('Failed to revoke grant:', error);
              Alert.alert('Error', 'Failed to revoke access');
            }
          },
        },
      ]
    );
  };

  const renderByPersonView = () => {
    if (byPersonData.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Image
            source={require('../../assets/icons/user.png')}
            style={[styles.emptyIcon, { tintColor: '#999' }]}
          />
          <Text style={styles.emptyText}>
            No access grants found
          </Text>
          <Text style={styles.emptySubtext}>
            You haven't shared any information yet
          </Text>
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {byPersonData.map((item, index) => (
          <View key={`person-${index}`} style={styles.personCard}>
            <View style={styles.personHeader}>
              <View style={styles.personInfo}>
                <Text style={styles.personName}>{item.person.name}</Text>
                <Text style={styles.personEmail}>{item.person.email}</Text>
                <View style={styles.badges}>
                  <View
                    style={[
                      styles.badge,
                      item.person.role === 'successor'
                        ? styles.successorBadge
                        : styles.keyholderBadge,
                    ]}
                  >
                    <Text style={styles.badgeText}>
                      {item.person.role === 'successor' ? 'Successor' : 'Keyholder'}
                    </Text>
                  </View>
                  {item.person.status === 'pending' && (
                    <View style={[styles.badge, styles.pendingBadge]}>
                      <Text style={styles.badgeText}>Pending</Text>
                    </View>
                  )}
                </View>
              </View>
              <Image
                source={require('../../assets/icons/user.png')}
                style={[styles.personIcon, { tintColor: '#29BFC0' }]}
              />
            </View>

            <View style={styles.grantsSection}>
              <Text style={styles.grantsSectionTitle}>
                Can View ({item.grants.length}):
              </Text>
              {item.grants.map(grant => (
                <View key={grant.grantId} style={styles.grantItem}>
                  <Image
                    source={require('../../assets/icons/check.png')}
                    style={[styles.checkIcon, { tintColor: '#4CAF50' }]}
                  />
                  <Text style={styles.grantText}>{grant.taskDisplayName}</Text>
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() =>
                      handleRevokeGrant(
                        grant.grantId,
                        item.person.name,
                        grant.taskDisplayName,
                      )
                    }
                  >
                    <Text style={styles.removeButtonText}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderByTaskView = () => {
    if (byTaskData.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Image
            source={require('../../assets/icons/file.png')}
            style={[styles.emptyIcon, { tintColor: '#999' }]}
          />
          <Text style={styles.emptyText}>
            No shared tasks found
          </Text>
          <Text style={styles.emptySubtext}>
            You haven't unlocked any tasks yet
          </Text>
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {byTaskData.map((item, index) => (
          <View key={`task-${index}`} style={styles.taskCard}>
            <View style={styles.taskHeader}>
              <Image
                source={require('../../assets/icons/file.png')}
                style={[styles.taskIcon, { tintColor: '#29BFC0' }]}
              />
              <Text style={styles.taskName}>{item.task.taskDisplayName}</Text>
            </View>

            <View style={styles.grantsSection}>
              <Text style={styles.grantsSectionTitle}>
                Shared with ({item.grants.length}):
              </Text>
              {item.grants.map(grant => (
                <View key={grant.grantId} style={styles.grantItem}>
                  <View style={styles.grantPersonInfo}>
                    <Image
                      source={require('../../assets/icons/user.png')}
                      style={[styles.smallUserIcon, { tintColor: '#29BFC0' }]}
                    />
                    <View style={styles.grantPersonText}>
                      <Text style={styles.grantText}>{grant.grantedToName}</Text>
                      <Text style={styles.grantSubtext}>{grant.grantedToEmail}</Text>
                    </View>
                    <View
                      style={[
                        styles.smallBadge,
                        grant.role === 'successor'
                          ? styles.successorBadge
                          : styles.keyholderBadge,
                      ]}
                    >
                      <Text style={styles.smallBadgeText}>
                        {grant.role === 'successor' ? 'S' : 'K'}
                      </Text>
                    </View>
                  </View>
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() =>
                      handleRevokeGrant(
                        grant.grantId,
                        grant.grantedToName,
                        item.task.taskDisplayName,
                      )
                    }
                  >
                    <Text style={styles.removeButtonText}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Access Management</Text>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.infoBox}>
        <Image
          source={require('../../assets/icons/lock.png')}
          style={[styles.infoIcon, { tintColor: '#1C2C4F' }]}
        />
        <Text style={styles.infoText}>
          Use the "Remove" button to revoke someone's access to information.
          To grant access, go to the specific task and use the unlock feature.
        </Text>
      </View>

      <View style={styles.toggleContainer}>
        <TouchableOpacity
          style={[
            styles.toggleButton,
            viewMode === 'by-person' && styles.toggleButtonActive,
          ]}
          onPress={() => setViewMode('by-person')}
        >
          <Text
            style={[
              styles.toggleText,
              viewMode === 'by-person' && styles.toggleTextActive,
            ]}
          >
            By Person
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.toggleButton,
            viewMode === 'by-task' && styles.toggleButtonActive,
          ]}
          onPress={() => setViewMode('by-task')}
        >
          <Text
            style={[
              styles.toggleText,
              viewMode === 'by-task' && styles.toggleTextActive,
            ]}
          >
            By Task
          </Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1C2C4F" />
          <Text style={styles.loadingText}>Loading access checklist...</Text>
        </View>
      ) : viewMode === 'by-person' ? (
        renderByPersonView()
      ) : (
        renderByTaskView()
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    padding: ms(16),
    marginHorizontal: ms(16),
    marginTop: ms(16),
    borderRadius: ms(15),
    gap: ms(12),
  },
  infoIcon: {
    width: s(24),
    height: s(24),
  },
  infoText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(20, 0.3),
  },
  toggleContainer: {
    flexDirection: 'row',
    marginHorizontal: ms(16),
    marginTop: ms(20),
    marginBottom: ms(16),
    gap: ms(12),
  },
  toggleButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    borderRadius: ms(50),
    backgroundColor: '#E0E0E0',
  },
  toggleButtonActive: {
    backgroundColor: '#29BFC0',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#666',
  },
  toggleTextActive: {
    color: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(20),
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: ms(32),
  },
  emptyIcon: {
    width: s(80),
    height: s(80),
  },
  emptyText: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(20),
  },
  emptySubtext: {
    fontSize: ms(16, 0.3),
    color: '#666',
    marginTop: ms(8),
    textAlign: 'center',
  },
  personCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(20),
    marginVertical: ms(8),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  personHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(16),
  },
  personInfo: {
    flex: 1,
  },
  personName: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  personEmail: {
    fontSize: ms(14, 0.3),
    color: '#666',
    marginTop: ms(4),
  },
  personIcon: {
    width: s(40),
    height: s(40),
  },
  badges: {
    flexDirection: 'row',
    marginTop: ms(10),
    gap: ms(8),
  },
  badge: {
    paddingHorizontal: ms(12),
    paddingVertical: ms(6),
    borderRadius: ms(20),
  },
  successorBadge: {
    backgroundColor: '#E8F5E9',
  },
  keyholderBadge: {
    backgroundColor: '#FFE8D6',
  },
  pendingBadge: {
    backgroundColor: '#FFF9C4',
  },
  badgeText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  smallBadge: {
    paddingHorizontal: ms(8),
    paddingVertical: ms(4),
    borderRadius: ms(12),
  },
  smallBadgeText: {
    fontSize: ms(11, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  grantsSection: {
    borderTopWidth: ms(1),
    borderTopColor: '#E0E0E0',
    paddingTop: ms(16),
    marginTop: ms(4),
  },
  grantsSectionTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  grantItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ms(10),
    gap: ms(12),
  },
  checkIcon: {
    width: s(20),
    height: s(20),
  },
  grantText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
  },
  grantSubtext: {
    fontSize: ms(13, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  grantPersonInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
  },
  grantPersonText: {
    flex: 1,
  },
  smallUserIcon: {
    width: s(20),
    height: s(20),
  },
  removeButton: {
    backgroundColor: '#E85C5C',
    paddingHorizontal: ms(16),
    paddingVertical: ms(8),
    borderRadius: ms(20),
  },
  removeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
  taskCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(20),
    marginVertical: ms(8),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(16),
    gap: ms(12),
  },
  taskIcon: {
    width: s(28),
    height: s(28),
  },
  taskName: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: ms(16),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
  },
});
